import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Label,
  ResponsiveContainer,
  Cell,
} from "recharts";

const StackedParentDomainChart = ({ domainScore }) => {
  const data =
    Array.isArray(domainScore) && domainScore.length ? domainScore : [];

  const getColor = [
    { color: "#FB0301", label: "< 90%" },
    { color: "#FFC100", label: "90% – 95%" },
    { color: "#009F52", label: "≥ 95%" },
  ];

  const getColorByScore = (score) => {
    if (score < 90) return getColor[0].color;
    if (score < 95) return getColor[1].color;
    return getColor[2].color;
  };

  const chartData = data.map((parent) => {
    const obj = { parentDomain: parent.parentDomain.trim() };
    const domains = Array.isArray(parent.domains) ? parent.domains : [];
    const sumDomainScores = domains.reduce(
      (acc, d) =>
        acc + (typeof d.qualityScore === "number" ? d.qualityScore : 0),
      0,
    );
    const parentScore =
      typeof parent.qualityScore === "number" ? parent.qualityScore : 0;
    const scale =
      sumDomainScores > 0 && parentScore > 0
        ? parentScore / sumDomainScores
        : 0;

    domains.forEach((domain) => {
      const key = domain.name.trim();
      const raw =
        typeof domain.qualityScore === "number" ? domain.qualityScore : 0;
      const scaled = scale > 0 ? Math.round(raw * scale * 100) / 100 : 0;
      obj[key] = scaled;
    });
    return obj;
  });

  const allDomainNames = [
    ...new Set(
      data.flatMap((p) => (p.domains || []).map((d) => d.name.trim())),
    ),
  ];

  // Custom tooltip (PER STACK SECTION)
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const { name, value, payload: row, fill } = payload[0];

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ccc",
          padding: "8px 10px",
          fontSize: 12,
          borderRadius: 4,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {row.parentDomain}
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span
            style={{
              width: 10,
              height: 10,
              backgroundColor: fill,
              display: "inline-block",
              marginRight: 6,
            }}
          />
          {name}: {value}
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: "100%", height: 420 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 5, left: 5, bottom: 14 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="parentDomain"
            tick={{
              fontSize: 10,
              fill: "#373d3f",
              textAnchor: "middle",
            }}
          >
            <Label
              value="Domain"
              offset={-10}
              position="insideBottom"
              style={{
                fontSize: "15px",
                fontWeight: 600,
                fontFamily: "Poppins, sans-serif",
                fill: "#373d3f",
              }}
            />
          </XAxis>

          <YAxis
            domain={[0, 100]}
            allowDataOverflow={false}
            tick={{ fontSize: 12, fill: "#373d3f" }}
          >
            <Label
              value="Quality Score"
              angle={-90}
              position="insideLeft"
              style={{
                fontSize: "15px",
                fontWeight: 600,
                fontFamily: "Poppins, sans-serif",
                fill: "#373d3f",
              }}
            />
          </YAxis>

          <Tooltip
            shared={false}
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
            content={<CustomTooltip />}
          />
          {allDomainNames.map((domain, i) => (
            <Bar
              key={domain}
              dataKey={domain}
              stackId="a"
              isAnimationActive={false}
              activeBar={{ fillOpacity: 1 }}
            >
              {i === allDomainNames.length - 1 && (
                <LabelList
                  dataKey={() => ""}
                  content={({ x, width, y, index }) => (
                    <text
                      x={x + width / 2}
                      y={y - 5}
                      textAnchor="middle"
                      fill="#000"
                      fontSize={12}
                    >
                      {data[index] ? data[index].qualityScore : ""}
                    </text>
                  )}
                />
              )}

              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorByScore(data[index]?.qualityScore || 0)}
                />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StackedParentDomainChart;
